<script setup>
import { ref, watch, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { mdiContentCopy, mdiCheckCircleOutline, mdiAlertOutline } from '@mdi/js';
import config from '../config';
import { registerWEC, savedWECID, saveWECID, forgetWECID, miningPassword } from '../services/aux';
import { copyText } from '../services/clipboard';

const emit = defineEmits(['identity-change']);
const route = useRoute();
const address = ref('');
const id = ref(savedWECID());
watch(id, value => emit('identity-change', value), { immediate: true });
const restored = ref(Boolean(id.value));
const stored = ref(true);
const busy = ref(false);
const error = ref('');
const remaining = ref(0);
const copied = ref(false);
const copyFailed = ref(false);
let controller;
let timer;
let disposed = false;

async function copy(value) {
  copyFailed.value = !await copyText(value);
  copied.value = true;
}

function forgetResult() {
  forgetWECID();
  id.value = '';
}

function cooldown(seconds) {
  clearInterval(timer);
  const until = Date.now() + seconds * 1000;
  remaining.value = seconds;
  timer = setInterval(() => {
    remaining.value = Math.max(0, Math.ceil((until - Date.now()) / 1000));
    if (!remaining.value) clearInterval(timer);
  }, 250);
}

async function submit() {
  if (busy.value || remaining.value || !address.value.trim()) return;
  const submittedAddress = address.value.trim();
  address.value = '';
  error.value = ''; busy.value = true;
  controller = new AbortController(); cooldown(60);
  try {
    const result = await registerWEC(config.auxRegistrationApiBase || '/api', submittedAddress, controller.signal);
    if (!disposed) {
      id.value = result.id;
      restored.value = false;
      stored.value = saveWECID(result.id);
    }
  } catch (e) {
    if (!disposed && e.name !== 'AbortError') {
      error.value = e.message;
      if (e.retryAfter) cooldown(e.retryAfter);
    }
  } finally { if (!disposed) busy.value = false; }
}

onUnmounted(() => { disposed = true; controller?.abort(); clearInterval(timer); address.value = ''; });
</script>

<template>
  <v-card id="wec" class="mp-setup" variant="outlined">
    <v-card-text class="mp-content">
      <h2 class="mp-heading mb-2">{{ $t('wec_about_title') }}</h2>
      <p class="mp-description mb-2">{{ $t('wec_about_description') }}</p>
      <v-alert class="wec-warning mt-3 mb-4" type="warning" color="#ffca6b" :icon="mdiAlertOutline" variant="tonal" border="start" density="compact" role="note">
        <h3 class="mp-subheading mb-1">{{ $t('wec_setup_warning_title') }}</h3>
        <p class="mp-description">{{ $t('wec_setup_warning_donation') }}</p>
        <p class="mp-description mt-2"><strong>{{ $t('wec_setup_warning_final') }}</strong></p>
      </v-alert>
      <details class="wec-about mb-4" :open="route.hash === '#wec'">
        <summary>{{ $t('wec_about_more') }}</summary>
        <dl class="wec-facts">
          <div><dt>{{ $t('wec_about_algorithm') }}</dt><dd>Equihash 200,9</dd></div>
          <div><dt>{{ $t('wec_about_block_time') }}</dt><dd>{{ $t('wec_about_block_time_value') }}</dd></div>
          <div><dt>{{ $t('wec_about_transfers') }}</dt><dd>{{ $t('wec_about_transfers_value') }}</dd></div>
          <div><dt>{{ $t('wec_about_maturity') }}</dt><dd>{{ $t('wec_about_maturity_value', { count: config.auxBlockConfirmations.WEC }) }}</dd></div>
        </dl>
        <p class="mp-description text-medium-emphasis">{{ $t('wec_about_payouts') }}</p>
        <nav class="wec-links" :aria-label="$t('wec_about_links')">
          <a href="https://w.cash/" target="_blank" rel="noopener noreferrer">{{ $t('wec_about_site') }}</a>
          <a href="https://wcashwallet.com/" target="_blank" rel="noopener noreferrer">{{ $t('wec_about_wallet') }}</a>
          <a href="https://wcashexplorer.com/" target="_blank" rel="noopener noreferrer">{{ $t('wec_about_explorer') }}</a>
        </nav>
      </details>
      <div class="mp-layout">
        <v-form class="mp-form" autocomplete="off" @submit.prevent="submit">
          <h3 class="mp-subheading">{{ $t('wec_setup_title') }}</h3>
          <v-text-field v-model="address" class="address-field" :label="$t('wec_address')" type="text" variant="filled" density="compact" autocomplete="off" spellcheck="false" autocapitalize="off" :disabled="busy" :maxlength="4096" hide-details="auto" required />
          <v-btn class="mp-submit" type="submit" color="#0d47a1" variant="flat" height="40" block :loading="busy" :disabled="busy || remaining > 0 || !address.trim()">
            {{ remaining > 0 ? $t('wec_wait', { seconds: remaining }) : $t('wec_get_id') }}
          </v-btn>
          <v-alert v-if="error" type="error" variant="tonal" density="compact">{{ $t(`wec_error_${error}`) }}</v-alert>
          <p class="mp-note text-medium-emphasis">{{ $t('wec_address_note') }}</p>
        </v-form>
        <aside class="mp-details" aria-live="polite">
          <template v-if="!id">
            <h3 class="mp-subheading">{{ $t('wec_how_to_use') }}</h3>
            <p class="mp-description text-medium-emphasis mt-1">{{ $t('wec_id_explanation') }}</p>
            <div class="mp-code-row mt-3"><code class="mp-id">Password: aux=&lt;id&gt;</code></div>
          </template>
          <div v-else class="mp-result">
            <h3 class="mp-subheading mb-2">{{ $t(restored ? 'wec_saved_result' : 'wec_created_result') }}</h3>
            <div class="mp-result-label">{{ $t('wec_your_id') }}</div>
            <div class="mp-code-row">
              <code class="mp-id">{{ id }}</code>
              <v-btn variant="text" icon size="small" :aria-label="$t('wec_copy_id')" :title="$t('wec_copy_id')" @click="copy(id)"><v-icon :icon="mdiContentCopy" /></v-btn>
            </div>
            <div class="mp-result-label">Password</div>
            <div class="mp-code-row">
              <code class="mp-id">{{ miningPassword(id) }}</code>
              <v-btn variant="text" icon size="small" :aria-label="$t('help_copy_password')" :title="$t('help_copy_password')" @click="copy(miningPassword(id))"><v-icon :icon="mdiContentCopy" /></v-btn>
            </div>
            <p class="mp-note mt-2" :class="stored ? 'text-medium-emphasis' : 'text-warning'">{{ $t(stored ? 'wec_generated_saved' : 'wec_error_storage_unavailable') }}</p>
            <v-btn variant="flat" color="orange-darken-1" size="small" class="mp-forget mt-2" @click="forgetResult">{{ $t('wec_forget') }}</v-btn>
          </div>
          <p class="mp-note text-medium-emphasis mt-3">{{ $t('wec_keep_zec_login') }}</p>
        </aside>
      </div>
    </v-card-text>
  </v-card>
  <v-snackbar v-model="copied" location="top" :color="copyFailed ? 'warning' : 'success'" :timeout="2000"><v-icon v-if="!copyFailed" :icon="mdiCheckCircleOutline" class="mr-1" />{{ $t(copyFailed ? 'help_copy_failed' : 'copy_successful') }}</v-snackbar>
</template>

<style scoped>
.mp-content { padding: 16px; }
.mp-heading { font-size: 18px; font-weight: 500; line-height: 1.4; }
.mp-description { font-size: 13px; line-height: 1.5; }
.mp-note { font-size: 12px; line-height: 1.5; }
.wec-warning :deep(.v-alert__content) { min-width: 0; overflow-wrap: anywhere; }
.wec-warning .mp-description { color: rgba(255, 255, 255, .9); }
.wec-about { font-size: 13px; line-height: 1.5; }
.wec-about summary { width: fit-content; max-width: 100%; cursor: pointer; padding-block: 4px; color: rgb(var(--v-theme-primary)); }
.wec-about summary:focus-visible { outline: 2px solid currentColor; outline-offset: 3px; border-radius: 2px; }
.wec-facts { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px 24px; margin-block: 12px; }
.wec-facts dt { color: rgba(255, 255, 255, .65); font-size: 12px; }
.wec-facts dd { margin: 2px 0 0; }
.wec-links { display: flex; flex-wrap: wrap; gap: 8px 20px; margin-top: 10px; }
.wec-links a { color: rgb(var(--v-theme-primary)); text-underline-offset: 3px; }
.mp-layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); align-items: start; gap: 32px; }
.mp-form { display: grid; align-content: start; gap: 12px; min-width: 0; }
.mp-form :deep(.v-field__input), .mp-form :deep(.v-field-label) { font-size: 14px; }
.mp-submit { border-radius: 4px; color: #fff !important; text-transform: none; font-size: 14px; letter-spacing: .06em; }
.mp-submit.v-btn--disabled :deep(.v-btn__overlay) { opacity: 0; }
.mp-details { min-width: 0; border-left: 1px solid rgba(255, 255, 255, .1); padding-left: 28px; }
.mp-subheading { font-size: 14px; font-weight: 600; line-height: 1.5; }
.mp-result-label { font-size: 12px; color: rgba(255, 255, 255, .65); margin-top: 10px; }
.mp-code-row { display: flex; align-items: center; gap: 6px; background: rgba(255, 255, 255, .035); padding: 4px 8px; border-radius: 4px; margin-top: 3px; }
.mp-id { flex: 1; font-family: "Nunito", sans-serif; font-size: 13px; user-select: all; overflow-wrap: anywhere; min-width: 0; }
.mp-result :deep(.v-btn--icon) { width: 30px; height: 30px; flex: 0 0 30px; }
.mp-result :deep(.v-btn--icon .v-icon) { font-size: 18px; }
.mp-forget { color: #1b1b1b; text-transform: none; letter-spacing: .02em; padding-inline: 12px; }
@media (max-width: 760px) {
  .wec-facts { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 16px; }
  .mp-content { padding: 12px; }
  .mp-layout { grid-template-columns: minmax(0, 1fr); gap: 20px; }
  .mp-details { border-left: 0; border-top: 1px solid rgba(255, 255, 255, .1); padding-left: 0; padding-top: 16px; }
}
</style>
